// Mirrors what PostgreSQL unaccent() strips beyond combining diacritics:
// precomposed Latin letters and ligatures that don't decompose via NFKD.
const LATIN_ASCII_MAP = {
  ß: 'ss',
  ẞ: 'SS',
  æ: 'ae',
  Æ: 'AE',
  œ: 'oe',
  Œ: 'OE',
  ø: 'o',
  Ø: 'O',
  đ: 'd',
  Đ: 'D',
  ð: 'd',
  Ð: 'D',
  ł: 'l',
  Ł: 'L',
  þ: 'th',
  Þ: 'Th',
  ħ: 'h',
  Ħ: 'H',
  ı: 'i',
};

export const stripDiacritics = s =>
  Array.from(s.normalize('NFKD').replace(/[̀-ͯ]/g, ''))
    .map(c => LATIN_ASCII_MAP[c] ?? c)
    .join('');

const normalizeForSearch = s => stripDiacritics(s).toLowerCase();

// What separates words: `_`, `-`, spaces and anything else that is not a
// letter, a digit or a mark (non-Latin scripts keep their marks after NFKD).
const WORD_SEPARATORS = /[^\p{L}\p{N}\p{M}]+/gu;

// Whether the term can be cut into pieces that each begin a word of the text,
// in order. Words may be skipped. Both are already normalized.
const matchesWordStarts = (text, term) => {
  const words = text.split(WORD_SEPARATORS).filter(Boolean);
  // Remembers the dead ends; without it, repeated words make the search exponential.
  const failed = new Uint8Array((term.length + 1) * (words.length + 1));

  const matchFrom = (termAt, wordAt) => {
    if (termAt === term.length) return true;
    if (wordAt === words.length) return false;
    const key = termAt * (words.length + 1) + wordAt;
    if (failed[key]) return false;

    const word = words[wordAt];
    let length = 0;
    while (length < word.length && word[length] === term[termAt + length]) {
      length += 1;
    }
    // Try the longest piece of this word first, then shorter ones ("akcen" is
    // akce_ceny only as "ak" + "cen"), then skip the word.
    for (; length > 0; length -= 1) {
      if (matchFrom(termAt + length, wordAt + 1)) return true;
    }
    if (matchFrom(termAt, wordAt + 1)) return true;

    failed[key] = 1;
    return false;
  };

  return matchFrom(0, 0);
};

/**
 * Whether `text` matches `term`, ignoring case and diacritics. It matches when
 * the text contains the term ("cekam" finds "čekám_na_prodejnu"), or when the
 * term is made of the starts of its words in order ("amc" finds
 * "ahoj_marek_cifra", "cekprod" finds "čekám_na_prodejnu"). A blank term
 * matches everything.
 *
 * @param {string} text - The text to search in.
 * @param {string} term - What the user typed.
 * @returns {boolean}
 */
export const matchesSearchTerm = (text, term) => {
  const normalizedTerm = normalizeForSearch((term || '').trim());
  if (!normalizedTerm) return true;

  const normalizedText = normalizeForSearch(String(text ?? ''));
  if (normalizedText.includes(normalizedTerm)) return true;

  // "ahoj marek" finds "ahoj_marek_cifra" as well. A term of separators only
  // ("_") would become empty and match everything, so it does not get here.
  const compactTerm = normalizedTerm.replace(WORD_SEPARATORS, '');
  return compactTerm !== '' && matchesWordStarts(normalizedText, compactTerm);
};
